import { useEffect } from 'react';

/**
 * "This is the hire you had open" — the mark Kanban, Gantt and Calendar put
 * on the hire the Table has expanded (`hooks/useHiresViewSwitch.js`'s
 * `openItemId`; owner, 2026-09-28: *"with an open order in tables it doesn't
 * seem to centralise on that order when I switch to kaban and gannt view"*).
 * ONE definition for all three views, so the mark cannot drift between them.
 *
 * 🎨 A blue RING — the app's own focus/primary blue, so it reads as "the one
 * you are on" and never as a status colour (red/amber/green all mean
 * something on these surfaces). Offset so it sits OUTSIDE the element and
 * cannot be mistaken for its border. Dark variant on both colours.
 * 🔑 Colour is never the only channel (root `CLAUDE.md`, the sole-carrier
 * rule): the ring is a GEOMETRY change (a 2px outline that was not there),
 * and `aria-current="true"` + `data-open-hire` carry it to assistive tech and
 * to tests. `title` is left to each view — they already own their tooltips.
 */
export const HIRE_OPEN_HIGHLIGHT =
  'ring-2 ring-blue-600 ring-offset-2 ring-offset-white dark:ring-blue-400 dark:ring-offset-gray-900';

/** The attributes for a hire element: nothing at all unless it is the open hire. */
export function openHireProps(itemId, openItemId) {
  if (openItemId == null || itemId !== openItemId) return {};
  return { 'aria-current': 'true', 'data-open-hire': 'true' };
}

/** `HIRE_OPEN_HIGHLIGHT` when this is the open hire, else `''`. */
export function openHireClass(itemId, openItemId) {
  return openItemId != null && itemId === openItemId ? HIRE_OPEN_HIGHLIGHT : '';
}

/**
 * Scroll the open hire into the middle of the view once the view has
 * rendered it (and again if the open hire changes). Finds it by
 * `data-open-hire`, inside `rootRef` when given — so a view never scrolls to
 * a mark another view drew. ⚠️ jsdom has no scroll: tests assert the call,
 * the real scroll is a browser check.
 */
export function useScrollOpenHireIntoView(openItemId, rootRef) {
  useEffect(() => {
    if (openItemId == null) return;
    const root = (rootRef && rootRef.current) || document;
    const el = root.querySelector('[data-open-hire="true"]');
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
    }
  }, [openItemId, rootRef]);
}
